import { type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { token } from '@atlaskit/tokens';
import Z_INDEX from '@databases/layout/z-index';
import { type CSSProperties } from 'react';

import { type PinnedLeadingFieldLayoutItem } from './compute-pinned-leading-layout';
import { PINNED_LEADING_BOUNDARY_BORDER_CSS_VARIABLE } from './pinned-leading-shadow';

export const PINNED_LEADING_DROP_INDICATOR_WIDTH_PX = 4;
export const PINNED_LEADING_BOUNDARY_DROP_TARGET_WIDTH_PX = 10;
export const PINNED_LEADING_BOUNDARY_SELECTOR =
  '[data-dbs-pinned-leading-freeze-boundary="true"]';

const PINNED_LEADING_FIELD_DRAG_Z_INDEX = Z_INDEX.TABLE_VIEW_DROP_INDICATOR + 1;

export const PINNED_LEADING_BOUNDARY_DROP_TARGET_INACTIVE_STYLE: CSSProperties = {
  position: 'absolute',
  top: 0,
  right: 0,
  width: `${PINNED_LEADING_BOUNDARY_DROP_TARGET_WIDTH_PX}px`,
  height: '100%',
  zIndex: Z_INDEX.TABLE_VIEW_DROP_INDICATOR + 1,
  pointerEvents: 'none'
};

export const PINNED_LEADING_BOUNDARY_DROP_TARGET_ACTIVE_STYLE: CSSProperties = {
  ...PINNED_LEADING_BOUNDARY_DROP_TARGET_INACTIVE_STYLE,
  pointerEvents: 'auto'
};

export function getPinnedLeadingHeaderCellStyle({
  isEnabled,
  layout,
  isSelectedFromPing,
  isSelectionsPerformanceEnabled,
  isFieldDragActive,
  maxPinnedLeadingFieldCount
}: {
  isEnabled: boolean;
  layout: PinnedLeadingFieldLayoutItem | undefined;
  isSelectedFromPing: boolean;
  isSelectionsPerformanceEnabled: boolean;
  isFieldDragActive: boolean;
  maxPinnedLeadingFieldCount: number;
}): CSSProperties | undefined {
  if (!isEnabled || !layout?.isPinned) {
    return undefined;
  }

  const selectedZIndex =
    isSelectionsPerformanceEnabled && isSelectedFromPing
      ? layout.headerZIndex + maxPinnedLeadingFieldCount + 1
      : layout.headerZIndex;
  const dragZIndex = isFieldDragActive
    ? PINNED_LEADING_FIELD_DRAG_Z_INDEX
    : layout.headerZIndex;
  const zIndex = Math.max(selectedZIndex, dragZIndex);

  return {
    position: 'sticky',
    // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Sticky offset is calculated from column widths.
    left: layout.stickyLeftPx,
    zIndex,
    backgroundColor: token('elevation.surface.sunken', '#F7F8F9'),
    borderRight: layout.isFreezeBoundary
      ? `var(${PINNED_LEADING_BOUNDARY_BORDER_CSS_VARIABLE}, none)`
      : undefined
  };
}

export function isPinnedLeadingDropIndicatorOccluded({
  cellElement,
  boundaryElement,
  closestEdge,
  isPinnedLeadingEnabled,
  isPinnedLeadingCell,
  isPinnedLeadingFreezeBoundary,
  isFirstScrollableAfterPinnedLeading
}: {
  cellElement: HTMLTableCellElement | null;
  boundaryElement: HTMLElement | null;
  closestEdge: Edge | null;
  isPinnedLeadingEnabled: boolean;
  isPinnedLeadingCell: boolean;
  isPinnedLeadingFreezeBoundary: boolean;
  isFirstScrollableAfterPinnedLeading: boolean;
}): boolean {
  if (
    !isPinnedLeadingEnabled ||
    !cellElement ||
    (closestEdge !== 'left' && closestEdge !== 'right')
  ) {
    return false;
  }

  if (isPinnedLeadingCell) {
    return false;
  }

  if (isPinnedLeadingFreezeBoundary && closestEdge === 'right') {
    return false;
  }

  if (isFirstScrollableAfterPinnedLeading && closestEdge === 'left') {
    return true;
  }

  if (!boundaryElement) {
    return false;
  }

  const cellRect = cellElement.getBoundingClientRect();
  const boundaryRect = boundaryElement.getBoundingClientRect();
  const boundaryOverlapPx =
    Math.min(cellRect.right, boundaryRect.right) -
    Math.max(cellRect.left, boundaryRect.left);

  if (boundaryOverlapPx > PINNED_LEADING_DROP_INDICATOR_WIDTH_PX) {
    return true;
  }

  if (
    closestEdge === 'left' &&
    cellRect.left <= boundaryRect.right + PINNED_LEADING_DROP_INDICATOR_WIDTH_PX
  ) {
    return true;
  }

  const indicatorX = closestEdge === 'right' ? cellRect.right : cellRect.left;

  return indicatorX <= boundaryRect.right + PINNED_LEADING_BOUNDARY_DROP_TARGET_WIDTH_PX;
}
